/* The qualification progression ladder — which level outranks which, and what a
 * sign-off at one level implies about the levels and skills beneath it.
 *
 * This used to be transcribed by hand in seven places (the matrix grid's valueGetter and
 * its cell renderer, the XLSX export, the MemberTraining dialog's banner, the hub's
 * qualification matrix, the hub aggregation endpoint, and the member-facing
 * qualifications page), in five different spellings — one of which hardcoded the JIBC
 * ladder for VIERA tenants too, and two of which carried a comparison bug. Every one of
 * them answered the same question, so they all read from here now. See #1622.
 *
 * Two directions are deliberately kept apart:
 *
 *  - READ implication (`impliedByLevel` / `isLevelComplete`) — a higher signed-off level
 *    satisfies a lower one. Pure derivation over the member's `skills` map; no writes.
 *  - WRITE cascade (`impliableCells`) — the set of cells an officer may credit when a
 *    member arrives already holding a level from another department. That set is a strict
 *    subset of "everything under the level"; see isImpliableSkill for what's held back
 *    and why.
 *
 * Beside the ladder sits a second, finer relation: SKILL_IMPLICATIONS (#1478) — one
 * individual qualification implying another regardless of which level each sits on.
 * Signing off Hazmat Ops means the member holds Hazmat Awareness; the ladder can't say
 * that, because it only speaks in whole levels.
 *
 * Deliberately takes the skills catalogue as an argument rather than requiring it: the
 * catalogue lives in ./index.js, which requires this module.
 */

const {canonicalQualificationPath, qualificationCellAt} = require('./sharedQualificationPaths');
const {COTR_LEVEL_MAP} = require('./skillDrillsCotr');

// The ladder per training provider, lowest rung first. VIERA (RDCK) runs a four-step
// progression starting at Truck Ready; generic VIERA starts at Exterior. NFPA 1010 (J&B)
// starts at Firefighter I; its external certificates live in Provincial Requirements.
// Anything not on a ladder (Team Leader, Fire Officer 1-4, Inspector/Investigator, and
// every `extra: true` certification category) implies nothing and is implied by nothing —
// those are parallel qualifications, not rungs.
const LEVEL_LADDERS = {
    viera: ['truckReady', 'exterior', 'interior', 'fullService'],
    vieraGeneric: ['exterior', 'interior', 'fullService'],
    jibc: ['exterior', 'interior', 'fullService'],
    nfpa1010: ['ff1', 'ff2'],
    cotr: ['exterior', 'interior', 'fullService']
};

// Certificates whose canonical storage category is independent of the level that
// requires them. Local curriculum cells (JIBC Knowledge, VIERA chapters, RDCK SOGs)
// are still discovered from each level's own catalogue below. This table adds only the
// shared certificates, preserving each provider's existing gate except for the explicit
// JIBC Gas/Electrical addition in #2325. Existing members with an Exterior roll-up remain
// qualified because an explicit level sign-off is authoritative; members qualifying from
// their individual cells going forward must meet the new gate.
const LEVEL_CERT_PATHS = {
    jibc: {
        exterior: [
            'provincial.ics100',
            'provincial.trafficControl',
            'provincial.gasSafety',
            'provincial.electricalSafety',
            'provincial.hazmatAwareness',
            'provincial.sppWff1'
        ],
        fullService: ['shared.hazmatOps']
    },
    viera: {
        truckReady: [
            'provincial.whmis',
            'provincial.trafficControl',
            'provincial.gasSafety',
            'provincial.electricalSafety',
            'provincial.hazmatAwareness'
        ],
        exterior: ['provincial.ics100', 'provincial.sppWff1'],
        fullService: ['shared.hazmatOps']
    },
    vieraGeneric: {
        exterior: [
            'provincial.ics100',
            'provincial.trafficControl',
            'provincial.gasSafety',
            'provincial.electricalSafety',
            'provincial.hazmatAwareness',
            'provincial.sppWff1'
        ],
        fullService: ['shared.hazmatOps']
    },
    nfpa1010: {
        ff1: [
            'provincial.whmis',
            'provincial.ics100',
            'provincial.trafficControl',
            'provincial.gasSafety',
            'provincial.electricalSafety',
            'provincial.hazmatAwareness'
        ],
        ff2: ['shared.hazmatOps']
    },
    cotr: Object.fromEntries(Object.entries(COTR_LEVEL_MAP)
        .map(([level, definition]) => [level, definition.certs.filter(cert => cert.required).map(cert => cert.path)])
        .filter(([, paths]) => paths.length))
};

const levelCertPaths = (levelKey, trainingProvider) =>
    (LEVEL_CERT_PATHS[trainingProvider] || LEVEL_CERT_PATHS[DEFAULT_TRAINING_PROVIDER])?.[levelKey] || [];

// Must match DEFAULT_TRAINING_PROVIDER in skillCatalog.js and
// api/src/routes/skillDrills.js — a department with no explicit provider gets the JIBC
// ladder everywhere or nowhere.
const DEFAULT_TRAINING_PROVIDER = 'jibc';

const levelLadder = trainingProvider =>
    LEVEL_LADDERS[trainingProvider] || LEVEL_LADDERS[DEFAULT_TRAINING_PROVIDER];

// Is this level's own roll-up cell signed off? An explicit non-COMPLETE status wins over a
// stray completedDate — the same precedence sectionCheckedOff uses, so the Requirements
// roster and the matrix can't disagree about one cell.
const isLevelCellComplete = (skills, levelKey) => {
    const cell = skills && skills[levelKey];
    if (!cell) return false;
    if (typeof cell === 'string') return cell === 'COMPLETE';
    return cell.status === 'COMPLETE' || (!cell.status && Boolean(cell.completedDate));
};

// The lowest higher level that satisfies this one, or null. Returns the KEY, not a label —
// callers hold the catalogue and label it themselves.
const impliedByLevel = (skills, levelKey, trainingProvider) => {
    // NFPA 1010 used Provincial Requirements as the rung beneath FF I/II. Keep
    // that read-time implication so an existing FF sign-off still paints the
    // Provincial level green, without putting the category into
    // levelsAtOrBelow (which would accidentally make every generic provincial
    // column a new FF I sign-off gate).
    if (trainingProvider === 'nfpa1010' && levelKey === 'provincial') {
        if (isLevelCellComplete(skills, 'ff1')) return 'ff1';
        if (isLevelCellComplete(skills, 'ff2')) return 'ff2';
    }
    const ladder = levelLadder(trainingProvider);
    const index = ladder.indexOf(levelKey);
    if (index < 0) return null;

    for (let i = index + 1; i < ladder.length; i++) {
        if (isLevelCellComplete(skills, ladder[i])) return ladder[i];
    }

    return null;
};

const isLevelComplete = (skills, levelKey, trainingProvider) =>
    isLevelCellComplete(skills, levelKey) || Boolean(impliedByLevel(skills, levelKey, trainingProvider));

// This level and every rung below it — what a sign-off at `levelKey` covers. Ordered
// lowest first, and empty for a level that isn't on the ladder at all.
const levelsAtOrBelow = (levelKey, trainingProvider) => {
    const ladder = levelLadder(trainingProvider);
    const index = ladder.indexOf(levelKey);
    return index < 0 ? [] : ladder.slice(0, index + 1);
};

const labelOf = (levelKey, catalogue) => {
    const def = catalogue && catalogue[levelKey];
    return def ? (def.label ?? levelKey) : levelKey;
};

const displayLevelForPath = (catalogue, path) => {
    for (const [levelKey, level] of Object.entries(catalogue || {})) {
        if (Object.values(level?.skills || {}).some(def =>
            def && typeof def === 'object' && def.skill === path)) return levelKey;
    }
    return null;
};

// Storage-only categories hold canonical cells but are not rendered. Find the
// provider-level alias as well so gates and credit roll-down still resolve the same
// stored cell if a caller supplied only the visible catalogue shape.
const definitionForPath = (catalogue, path) => {
    const [category, key] = path.split('.');
    const canonical = catalogue?.[category]?.skills?.[key];
    if (canonical !== undefined) return canonical;

    for (const level of Object.values(catalogue || {})) {
        for (const def of Object.values(level?.skills || {})) {
            if (def && typeof def === 'object' && def.skill === path) {
                const {skill: _skill, ...definition} = def;
                return definition;
            }
        }
    }

    return undefined;
};

// Which levels a credit at `levelKey` covers. Two shapes of qualification sit side by side
// in the catalogue and they cascade differently:
//
//  - LADDER rungs (Exterior → Interior → Full Service, with Truck Ready beneath them on
//    VIERA). Crediting one covers every rung below it — that's what the progression means.
//  - STANDALONE qualifications (Team Leader, Fire Officer 1-4, Inspector/Investigator).
//    They hold skills of their own, so a credit still fills those in, but they sit BESIDE
//    the ladder rather than on it: a Fire Officer 2 credit says nothing about Interior, and
//    finishing Full Service says nothing about Team Leader.
//
// `extra: true` categories (Rescue Training, Wildland, the vendor buckets) are collections
// of unrelated certificates rather than a qualification, so there is nothing to credit as a
// whole and they return empty.
const levelsCoveredByCredit = (levelKey, trainingProvider, catalogue = {}) => {
    const ladder = levelsAtOrBelow(levelKey, trainingProvider);
    if (ladder.length) return ladder;

    const def = catalogue[levelKey];
    return def && !def.extra ? [levelKey] : [];
};

// Every level an officer may credit, ladder rungs first (lowest to highest) and the
// standalone qualifications after, in catalogue order — what the level picker offers.
const creditableLevels = (trainingProvider, catalogue = {}) => {
    const ladder = levelLadder(trainingProvider).filter(level => catalogue[level]);
    const standalone = Object.keys(catalogue)
        .filter(level => !ladder.includes(level) && !catalogue[level].extra);

    return [...ladder, ...standalone];
};

// May a level sign-off credit this skill cell? Three kinds of cell are held back, and the
// reasons are different in each case:
//
//  - An ALIAS (`skill: 'exterior.fr3'`) is normally a second column onto a cell that lives
//    elsewhere. Writing it would record the same qualification twice. A placement alias
//    explicitly marked `requiredForLevel: true` is the exception: generic VIERA uses that
//    to show its canonical `truckReady.*` introductory chapters inside Exterior.
//  - An EXPIRING cert can't be credited without an expiry date, and we don't have one. A
//    bare COMPLETE would leave the member holding a First Aid ticket that the expiry sweep
//    can never age out — worse than no record at all.
//  - `impliedByLevel: false` marks a cell that simply isn't implied by the qualification:
//    a driver's licence or the OFC Driver/Operator check sheet, which is the completion
//    cell of a STANDALONE skill-drill section (#1579) and by design neither implies nor is
//    implied by a level.
//
// Everything else is fair game, but the officer still confirms it cell by cell — some
// entries (department SOGs, RDCK HR orientation) are things a lateral hire genuinely
// hasn't done, and that's a judgement call rather than a catalogue fact.
const isImpliableSkill = def => {
    const config = def && typeof def === 'object' ? def : {};
    if (config.skill && config.requiredForLevel !== true) return false;
    if (config.hasExpiry) return false;
    if (config.impliedByLevel === false) return false;
    return true;
};

// Every cell a sign-off at `levelKey` may credit, lowest level first and in catalogue
// order within each level. `catalogue` is the department's resolved skills catalogue (what
// buildSkills() returns) — VIERA and JIBC have different levels under the same keys, so
// passing the wrong one silently yields cells the member's record has no room for.
//
// `theoryPractical` / `theoryOnly` ride along because a cell with only one half written is
// repainted in-progress amber by the matrix, so the writer has to set both or neither.
const impliableCells = (levelKey, trainingProvider, catalogue = {}) => {
    const cells = [];

    for (const level of levelsCoveredByCredit(levelKey, trainingProvider, catalogue)) {
        const skills = (catalogue[level] || {}).skills || {};

        for (const [skillKey, def] of Object.entries(skills)) {
            if (!isImpliableSkill(def)) continue;

            const config = def && typeof def === 'object' ? def : {label: def};
            const [storedLevel, storedSkillKey] = (config.skill || `${level}.${skillKey}`).split('.');
            cells.push({
                level: storedLevel,
                levelLabel: labelOf(level, catalogue),
                skillKey: storedSkillKey,
                label: config.label || skillKey,
                theoryPractical: Boolean(config.theoryPractical),
                theoryOnly: Boolean(config.theoryOnly)
            });
        }

        for (const path of levelCertPaths(level, trainingProvider)) {
            const [certLevel, skillKey] = path.split('.');
            const config = definitionForPath(catalogue, path);
            if (!config || !isImpliableSkill(config)) continue;
            cells.push({
                level: certLevel,
                levelLabel: labelOf(certLevel === 'shared' ? level : certLevel, catalogue),
                skillKey,
                label: (typeof config === 'object' && config.label) || config || skillKey,
                theoryPractical: false,
                theoryOnly: false
            });
        }
    }

    return cells.filter((cell, index) =>
        cells.findIndex(candidate => candidate.level === cell.level && candidate.skillKey === cell.skillKey) === index);
};

const isImpliableCell = (levelKey, trainingProvider, catalogue, level, skillKey) =>
    impliableCells(levelKey, trainingProvider, catalogue)
        .some(cell => cell.level === level && cell.skillKey === skillKey);

// ===========================================================================
// Level certifications (#1450)
//
// A level holds two kinds of cell: the drill chapters the Requirements sheet walks a member
// through, and CERTIFICATES they hold from elsewhere (WHMIS, ICS 100, NFPA 1072 Hazmat Ops,
// the provincial safety tickets). The second kind is never signed off on the sheet, so it
// has to be checked separately before the level can be completed.

// Is this catalogue entry a certification the level's sign-off must require? Four kinds of
// cell are not:
//
//  - A drill CHAPTER (`chapter: 14`) — signed off drill by drill on the sheet, and the
//    sign-off route checks those itself.
//  - An ALIAS (`skill: 'exterior.fr3'`) — a second column onto a cell that lives elsewhere,
//    so requiring it would demand the same certificate twice.
//  - `impliedByLevel: false` — a cell that isn't part of the qualification at all. The OFC
//    Driver/Operator check sheet is the completion cell of a STANDALONE section (#1579); a
//    member doesn't need a driving ticket to be Exterior-qualified.
//  - `requiredForLevel: false` — an explicit opt-out for entries that live on a level for
//    presentation but aren't a condition of holding it. RDCK's HR orientation is the case
//    that forced this: it's district administration, not a firefighting qualification, and
//    gating on it would hard-block every non-RDCK VIERA department from ever completing
//    Exterior. (isImpliableSkill's note about SOGs and HR orientation is the same judgement
//    seen from the credit side.)
const isLevelCert = def => {
    const config = def && typeof def === 'object' ? def : {};
    if (config.chapter) return false;
    if (config.skill && config.requiredForLevel !== true) return false;
    if (config.impliedByLevel === false) return false;
    if (config.requiredForLevel === false) return false;
    return true;
};

// Every certification required to hold `levelKey`, with whether this member has it.
//
// Resolved across `levelsAtOrBelow`, NOT the single level — that is the fix for #1450. On
// VIERA, Truck Ready is a rung beneath Exterior with no Requirements section of its own
// (SECTION_KEYS is exterior/interior/fullService), so nothing ever passed 'truckReady' here
// and its seven provincial certs were required by nothing at all — while signing off
// Exterior made the matrix render Truck Ready COMPLETE via impliedByLevel. Gating Exterior
// on the levels it implies closes that hole. On JIBC the ladder has no truckReady rung, so
// this resolves to exactly what it did before.
//
// A level that isn't on the ladder (a standalone section's key) falls back to itself, which
// is what the single-key lookup did.
const levelCerts = (skills, levelKey, trainingProvider, catalogue = {}) => {
    return levelRequirements(skills, levelKey, trainingProvider, catalogue, isLevelCert);
};

// The matrix's inferred level state must include every curriculum column, including drill
// chapters. The API sign-off gate uses levelCerts because it validates drills separately;
// renderers, exports and Ask use this complete view so certificates alone cannot make a
// member appear to hold a level they have done no drill work for.
const isLevelCompletionCell = def => {
    const config = def && typeof def === 'object' ? def : {};
    if (config.skill && config.requiredForLevel !== true) return false;
    if (config.impliedByLevel === false) return false;
    if (config.requiredForLevel === false) return false;
    return true;
};

const cellIsComplete = cell => {
    if (typeof cell === 'string') return cell === 'COMPLETE';
    if (!cell) return false;
    return cell.status === 'COMPLETE' || (!cell.status && Boolean(cell.completedDate));
};

const levelRequirements = (skills, levelKey, trainingProvider, catalogue = {}, includeLocal = isLevelCert) => {
    const levels = levelsAtOrBelow(levelKey, trainingProvider);
    const certs = [];

    for (const level of (levels.length ? levels : [levelKey])) {
        // A signed-off lower rung is the durable record that its own requirements were
        // satisfied. Count its requirements as complete even when those underlying cells
        // are absent: older imports often carry the roll-up only, and a curriculum can
        // change after the member earned it. Keep the cells in the result so progress
        // reporting retains a stable numerator and denominator. The target level itself
        // must still be inferred from its cells, otherwise this helper would accept the
        // very roll-up it is deciding whether to award.
        const lowerLevelComplete = level !== levelKey && isLevelComplete(skills, level, trainingProvider);

        const levelSkills = (catalogue[level] || {}).skills || {};

        for (const [key, def] of Object.entries(levelSkills)) {
            if (!includeLocal(def)) continue;

            const config = def && typeof def === 'object' ? def : {label: def};
            const storedPath = config.skill || `${level}.${key}`;
            const [storedLevel, storedKey] = storedPath.split('.');
            const cell = qualificationCellAt(skills, storedPath, trainingProvider);
            certs.push({
                level: storedLevel,
                levelLabel: labelOf(level, catalogue),
                key: storedKey,
                label: config.label || key,
                complete: lowerLevelComplete || cellIsComplete(cell)
            });
        }

        for (const path of levelCertPaths(level, trainingProvider)) {
            const [certLevel, key] = path.split('.');
            const def = definitionForPath(catalogue, path);
            if (!def) continue;
            const config = typeof def === 'object' ? def : {label: def};
            const cell = qualificationCellAt(skills, path, trainingProvider);
            certs.push({
                level: certLevel,
                levelLabel: labelOf(certLevel === 'shared' ? level : certLevel, catalogue),
                key,
                label: config.label || key,
                complete: lowerLevelComplete || cellIsComplete(cell),
                requiredAt: level
            });
        }
    }

    return certs.filter((cert, index) =>
        certs.findIndex(candidate => candidate.level === cert.level && candidate.key === cert.key) === index);
};

const levelCompletionCells = (skills, levelKey, trainingProvider, catalogue = {}) =>
    levelRequirements(skills, levelKey, trainingProvider, catalogue, isLevelCompletionCell);

// The single inferred-level rule used by the matrix, export, Ask and edit dialog.
// A level needs at least one local curriculum cell (a list of external certificates
// cannot award a level by itself), and every local/shared requirement must be complete.
const areAllLevelRequirementsComplete = (skills, levelKey, trainingProvider, catalogue = {}) => {
    const requirements = levelCompletionCells(skills, levelKey, trainingProvider, catalogue);
    return requirements.some(requirement => requirement.requiredAt === undefined)
        && requirements.every(requirement => requirement.complete);
};

// The labels of the certifications still outstanding — what both sign-off gates report.
const outstandingLevelCerts = (skills, levelKey, trainingProvider, catalogue = {}) =>
    levelCerts(skills, levelKey, trainingProvider, catalogue)
        .filter(cert => !cert.complete)
        .map(cert => cert.label);

// ===========================================================================
// Skill-to-skill implication (#1478)
//
// A prerequisite qualification that the superior one subsumes: NFPA 1072 makes Awareness a
// prerequisite of Ops and Ops a prerequisite of Technician, so a member signed off on
// Hazmat Ops demonstrably holds Hazmat Awareness. RDCK described the same thing on the
// 2026-07-30 regional call, as Vector does it: "if you complete hazmat ops, hazmat
// awareness would also be checked off".
//
// This is NOT the level ladder, and it can't be expressed as one:
//
//  - It crosses levels in both directions. Hazmat Awareness sits on Exterior (Truck Ready
//    on VIERA) while Hazmat Ops sits on Full Service, and ICS runs Exterior → Team Leader →
//    Fire Officer 2 → Fire Officer 3.
//  - It reaches into `extra: true` categories, which `levelsCoveredByCredit` returns
//    nothing for by design. Hazmat Technician lives in Rescue Training, so the ladder can
//    never credit anything from it.
//  - It's per-cell, not per-level. Signing off the single Hazmat Ops cell has to cascade
//    even when nothing else on Full Service is done.
//
// Keys are `level.skillKey` paths, values the qualifications DIRECTLY beneath. The closure
// is computed (see impliedSkillCells), so a chain is written one link at a time — a
// Technician entry names Ops, not Ops and Awareness both.
//
// Deliberately hand-authored and small. There are plenty of other plausible pairs in the
// catalogue (Fire Inspector 1/2, SCBA Technician 1/2, ESM 1/2, the Fire Officer 1-4 course
// pairs, the instructor certs, the BCWS wildland chains) and they are NOT here: for most of
// them holding the superior course is a prerequisite you had to satisfy, not a certificate
// the superior course awards you. Each pair is a claim about what a department may record
// as earned, so each one gets added deliberately rather than inferred from a naming
// convention.
const SHARED_SKILL_IMPLICATIONS = {
    // ICS Canada runs a strict prerequisite chain — you cannot sit ICS 200 without ICS 100,
    // 300 without 200, or 400 without 300 — and each course is issued as its own
    // certificate, so the roll-down records what the member genuinely holds.
    'teamLeader.ics200': ['provincial.ics100'],
    'fireOfficer2.ics300': ['teamLeader.ics200'],
    'fireOfficer3.ics400': ['fireOfficer2.ics300'],

    // NFPA 1006 awareness → operations → technician, the same three-tier shape as hazmat.
    // High Angle has no Awareness cell in the catalogue; Low Angle is a separate course
    // rather than the rung beneath High Angle Ops, so it isn't wired in.
    'rescueTraining.swiftWaterOps': ['rescueTraining.swiftWaterAwareness'],
    'rescueTraining.swiftWaterTech': ['rescueTraining.swiftWaterOps'],
    'rescueTraining.confinedSpaceOps': ['rescueTraining.confinedSpaceAwareness'],
    'rescueTraining.confinedSpaceTech': ['rescueTraining.confinedSpaceOps'],
    'rescueTraining.forcibleEntryOps': ['rescueTraining.forcibleEntryAwareness'],
    'rescueTraining.forcibleEntryTech': ['rescueTraining.forcibleEntryOps'],
    'rescueTraining.highAngleTech': ['rescueTraining.highAngleOps'],

    // Provider names and ladder placement differ, but these are the same certificates.
    'shared.hazmatOps': ['provincial.hazmatAwareness'],
    'rescueTraining.hazmatTech': ['shared.hazmatOps']
};

// Shared certificate paths make the implication graph provider-independent. Keep the
// keyed shape because callers select a graph by provider and because provider-specific
// implications may still be added later.
const SKILL_IMPLICATIONS = {
    jibc: SHARED_SKILL_IMPLICATIONS,
    viera: SHARED_SKILL_IMPLICATIONS,
    vieraGeneric: SHARED_SKILL_IMPLICATIONS,
    nfpa1010: SHARED_SKILL_IMPLICATIONS,
    cotr: SHARED_SKILL_IMPLICATIONS
};

const skillImplications = trainingProvider =>
    SKILL_IMPLICATIONS[trainingProvider] || SKILL_IMPLICATIONS[DEFAULT_TRAINING_PROVIDER];

// May a skill sign-off credit this cell? Two of isImpliableSkill's three guards apply for
// the same reasons — an ALIAS (`skill: 'exterior.fr3'`) would record one qualification
// twice, and an EXPIRING cert credited without an expiry date can never be aged out by the
// expiry sweep.
//
// `impliedByLevel: false` is deliberately NOT consulted. That flag answers a different
// question — "does a LEVEL sign-off reach this cell" — and the two cells carrying it
// (Driver/Operator, the FR ticket) are held back from the ladder because a level says
// nothing about them, not because they can never be implied at all. A named pair in
// SKILL_IMPLICATIONS is a specific claim about two specific qualifications, so it isn't
// governed by that flag.
const isImpliedSkillCreditable = def => {
    const config = def && typeof def === 'object' ? def : {};
    if (config.skill) return false;
    if (config.hasExpiry) return false;
    return true;
};

// Every qualification a sign-off at `level.skillKey` implies, nearest first — the full
// transitive closure, so Hazmat Technician yields Ops AND Awareness. Cells the department's
// catalogue doesn't have (a pair naming the other provider's key) and cells that can't be
// credited (see isImpliedSkillCreditable) are dropped, so the result is always writable
// as-is. Empty for a cell with nothing beneath it, which is nearly all of them.
//
// `theoryPractical` / `theoryOnly` ride along for the same reason impliableCells carries
// them: a cell with only one half written is repainted in-progress amber by the matrix.
const impliedSkillCells = (level, skillKey, trainingProvider, catalogue = {}) => {
    const graph = skillImplications(trainingProvider);
    const cells = [];
    // Accept provider-specific paths during the deploy-before-migrate window, but
    // always traverse and return the shared vocabulary. This also keeps older
    // clients useful while the API has already moved to canonical paths.
    const sourcePath = canonicalQualificationPath(`${level}.${skillKey}`);
    const seen = new Set([sourcePath]);
    const queue = [...(graph[sourcePath] || [])];

    while (queue.length) {
        const path = queue.shift();
        if (seen.has(path)) continue;
        seen.add(path);
        // Walk on regardless of whether this cell is creditable — a link the catalogue
        // doesn't carry must not sever the chain behind it.
        queue.push(...(graph[path] || []));

        const [cellLevel, cellKey] = path.split('.');
        const def = definitionForPath(catalogue, path);
        if (!def || !isImpliedSkillCreditable(def)) continue;

        const config = typeof def === 'object' ? def : {label: def};
        cells.push({
            level: cellLevel,
            levelLabel: labelOf(displayLevelForPath(catalogue, path) || cellLevel, catalogue),
            skillKey: cellKey,
            label: config.label || cellKey,
            theoryPractical: Boolean(config.theoryPractical),
            theoryOnly: Boolean(config.theoryOnly)
        });
    }

    return cells;
};

// Does this cell already hold something an officer put there? A credit must never overwrite
// a real record — a licence number, an expiry, a drill sign-off, or a status somebody set by
// hand. Cells a previous credit wrote ARE overwritable, so re-running one with a corrected
// date works — that goes for either kind of credit, and a level sign-off overwriting a
// weaker skill implication is the right way round.
//
// Both sides of the credit read this: the server skips such cells, and the confirmation
// dialog shows them locked rather than offering a choice the write wouldn't honour.
const cellHasOwnRecord = cell => {
    if (!cell) return false;
    if (typeof cell === 'string') return cell !== 'INCOMPLETE';
    if (cell.source === 'levelSignOff' || cell.source === 'skillImplication') return false;

    return Boolean(
        (cell.status && cell.status !== 'INCOMPLETE') ||
        cell.completedDate ||
        cell.practical ||
        cell.theory ||
        cell.expiryDate ||
        cell.licenceNumber
    );
};

module.exports = {
    LEVEL_LADDERS,
    LEVEL_CERT_PATHS,
    levelLadder,
    isLevelCellComplete,
    impliedByLevel,
    isLevelComplete,
    levelsAtOrBelow,
    levelsCoveredByCredit,
    creditableLevels,
    isImpliableSkill,
    impliableCells,
    isImpliableCell,
    isLevelCert,
    levelCerts,
    levelCompletionCells,
    areAllLevelRequirementsComplete,
    outstandingLevelCerts,
    SKILL_IMPLICATIONS,
    skillImplications,
    isImpliedSkillCreditable,
    impliedSkillCells,
    cellHasOwnRecord
};
